import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useTranslation } from '@/core/i18n';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/theme/components/ui/popover';
import { useAuth } from '@/core/auth/useAuth';
import { canAccessCompanyScope } from '@/core/auth/modulePermissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocation as useLocationEntity } from '@/entities/location/hooks/useLocations';
import {
  useNotifications,
  useUnreadCounts,
  useMarkAllAsRead,
  useMarkAsRead,
  type AppNotification,
  type NotificationScope,
} from '@/modules/notifications';

const RECENT_LIMIT = 10;

/** Per-type chrome for the small circular icon at the left of each row.
 *  Mirrors the mockup's ic.coral / ic.sage / ic.ocean / default sand. */
const TYPE_TILE: Record<AppNotification['type'], string> = {
  success: 'bg-sage-100 text-sage-700',
  warning: 'bg-sun-100 text-sun-700',
  error: 'bg-coral-100 text-coral-700',
  info: 'bg-ocean-100 text-ocean-700',
};

/** Single-character glyph by type — keeps the trigger compact and
 *  echoes the mockup which uses '!', '◑', '€', '◇'. */
const TYPE_GLYPH: Record<AppNotification['type'], string> = {
  success: '✓',
  warning: '!',
  error: '!',
  info: '◑',
};

/** Scope accents: sand for you, ocean for the site, violet for the company —
 *  the same three the rest of the deck uses to say where you are. */
const TAB_ACCENT: Record<NotificationScope, string> = {
  user: 'border-b-sand-500',
  location: 'border-b-ocean-500',
  company: 'border-b-violet-500',
};

const TAB_COUNT_ACCENT: Record<NotificationScope, string> = {
  user: 'bg-sand-200 text-sand-700',
  location: 'bg-ocean-100 text-ocean-700',
  company: 'bg-violet-100 text-violet-700',
};

function formatTimeAgo(value: Date, now: Date): string {
  const diffMs = now.getTime() - new Date(value).getTime();
  const seconds = Math.max(1, Math.floor(diffMs / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

export interface NotificationsBellProps {
  className?: string;
}

export function NotificationsBell({ className }: NotificationsBellProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocationEntity(activeLocationId ?? undefined);
  const [scope, setScope] = useState<NotificationScope>('user');

  const { data: counts } = useUnreadCounts();
  const { data: notifications = [] } = useNotifications({
    scope,
    status: 'open',
    locationId: scope === 'location' ? activeLocationId ?? undefined : undefined,
  });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = counts?.total ?? 0;
  const recent = notifications.slice(0, RECENT_LIMIT);
  const now = new Date();

  const canReachCompanyScope = canAccessCompanyScope(user);

  // The location tab only means something inside a location. Outside one
  // there is no site whose queue this would be, so it is dropped rather than
  // shown empty — a permanently empty tab teaches people to skip it.
  const tabs: { scope: NotificationScope; label: string; count: number }[] = [
    {
      scope: 'user',
      label: t('notifications.scope.mine', 'Mine'),
      count: counts?.user ?? 0,
    },
    ...(activeLocationId
      ? [
          {
            scope: 'location' as const,
            label: activeLocation?.name ?? t('notifications.scope.location', 'This location'),
            count: counts?.location ?? 0,
          },
        ]
      : []),
    // Same reason the location tab is conditional: the company inbox is a
    // surface a location-only operator cannot reach. Its "View all" link
    // lands on /company/notifications, which CompanyPage answers with
    // /access-denied — an affordance that only ever fails.
    ...(canReachCompanyScope
      ? [
          {
            scope: 'company' as const,
            label: t('notifications.scope.company', 'Company'),
            count: counts?.company ?? 0,
          },
        ]
      : []),
  ];

  const viewAllHref =
    scope === 'location' && activeLocationId
      ? `/location/${activeLocationId}/notifications`
      : scope === 'company'
        ? '/company/notifications'
        : '/notifications';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('notifications.bell.aria', 'Notifications')}
          className={cn(
            'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-sand-700 transition-colors hover:bg-sand-100 hover:text-sand-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-500/40',
            className,
          )}
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-coral-500 px-1 font-mono text-[10px] font-semibold text-sand-50"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[344px] overflow-hidden p-0">
        {/* Ocean-tinted header strip — matches the mockup's .np-head. */}
        <div className="border-b border-sand-200 bg-gradient-to-b from-ocean-50 to-card px-4 pt-3">
          <div className="flex items-baseline justify-between">
            <h3 className="font-serif text-[14px] font-semibold tracking-[-0.005em] text-sand-900">
              {unreadCount > 0
                ? t('notifications.bell.titleUnread', 'Inbox · {{count}} unread', { count: unreadCount })
                : t('notifications.bell.title', 'Notifications')}
            </h3>
            <button
              type="button"
              onClick={() => markAllAsRead.mutate({ scope, status: 'open' })}
              disabled={markAllAsRead.isPending || unreadCount === 0}
              className="text-[11px] font-semibold text-ocean-700 transition-colors hover:text-ocean-800 disabled:cursor-not-allowed disabled:text-sand-400"
            >
              {t('notifications.bell.markAll', 'Mark all read')}
            </button>
          </div>

          <div
            className="mt-2 flex gap-0.5"
            role="tablist"
            aria-label={t('notifications.scope.aria', 'Notification scope')}
          >
            {tabs.map((tab) => (
              <button
                key={tab.scope}
                type="button"
                role="tab"
                aria-selected={scope === tab.scope}
                onClick={() => setScope(tab.scope)}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-2.5 pb-2 pt-1.5 text-[12px] font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-500/40',
                  scope === tab.scope
                    ? cn('text-sand-900', TAB_ACCENT[tab.scope])
                    : 'text-sand-600 hover:text-sand-900',
                )}
              >
                <span className="max-w-[92px] truncate">{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 font-mono text-[10px] font-semibold',
                      scope === tab.scope ? TAB_COUNT_ACCENT[tab.scope] : 'bg-sand-100 text-sand-600',
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {recent.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-sand-600">
              {t('notifications.bell.empty', 'No notifications yet.')}
            </p>
          ) : (
            <ul>
              {recent.map((notification) => {
                const tileClass = TYPE_TILE[notification.type];
                const glyph = TYPE_GLYPH[notification.type];
                const target = notification.actionUrl ?? viewAllHref;
                return (
                  <li key={notification.id}>
                    <Link
                      to={target}
                      onClick={() => {
                        if (!notification.isRead) markAsRead.mutate(notification.id);
                      }}
                      className={cn(
                        'grid grid-cols-[32px_1fr_auto] items-start gap-3 border-b border-dashed border-sand-200 px-4 py-2.5 text-[12.5px] transition-colors hover:bg-sand-50 last:border-b-0',
                        !notification.isRead &&
                          (notification.scope === 'company'
                            ? 'border-l-[3px] border-l-violet-500 bg-gradient-to-r from-violet-50 to-transparent pl-[13px]'
                            : 'border-l-[3px] border-l-ocean-500 bg-gradient-to-r from-ocean-50 to-transparent pl-[13px]'),
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-[30px] w-[30px] items-center justify-center rounded-full font-mono text-[14px] font-semibold',
                          tileClass,
                        )}
                      >
                        {glyph}
                      </span>
                      <div className="min-w-0">
                        <h5
                          className={cn(
                            'truncate text-[12.5px] leading-snug',
                            notification.isRead ? 'font-medium text-sand-800' : 'font-semibold text-sand-900',
                          )}
                        >
                          {notification.title}
                        </h5>
                        <p className="mt-0.5 line-clamp-2 text-[10.5px] text-muted-foreground">
                          {notification.message}
                        </p>
                      </div>
                      <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                        {formatTimeAgo(notification.createdAt, now)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-sand-200 bg-sand-50 px-4 py-2.5">
          <Link
            to={viewAllHref}
            className="block text-center text-[11.5px] font-semibold text-ocean-700 transition-colors hover:text-ocean-800"
          >
            {t('notifications.bell.viewAll', 'View all notifications →')}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationsBell;
